import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import cache from "utils/cache";
import CartDrawer from "components/CartDrawer";

const CartDrawerContext = createContext(null);

const isSameCartItem = (left, right) =>
  left.productId === right.productId &&
  (left.variantId || null) === (right.variantId || null);

export const CartDrawerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useState(() => cache.getItem("cartItems") || []);

  const persistCartItems = useCallback((items) => {
    setCartItems(items);
    cache.setItem("cartItems", items);
  }, []);

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    cache.setItem("cartItems", []);
    setIsOpen(false);
  }, []);

  const addToCart = useCallback(
    (item) => {
      setCartItems((prev) => {
        const existingIndex = prev.findIndex((cartItem) => isSameCartItem(cartItem, item));
        const next =
          existingIndex >= 0
            ? prev.map((cartItem, index) =>
                index === existingIndex
                  ? {
                      ...cartItem,
                      productQuantity:
                        Number(cartItem.productQuantity || 0) +
                        Number(item.productQuantity || 0),
                    }
                  : cartItem
              )
            : [...prev, item];
        cache.setItem("cartItems", next);
        return next;
      });
      setIsOpen(true);
    },
    []
  );

  const removeFromCart = useCallback(
    (index) => {
      setCartItems((prev) => {
        const next = prev.filter((_, itemIndex) => itemIndex !== index);
        cache.setItem("cartItems", next);
        return next;
      });
    },
    []
  );

  const updateCartItemQuantity = useCallback(
    (index, quantity) => {
      const nextQuantity = Math.max(1, Number(quantity) || 1);
      setCartItems((prev) => {
        const next = prev.map((item, itemIndex) =>
          itemIndex === index ? { ...item, productQuantity: nextQuantity } : item
        );
        cache.setItem("cartItems", next);
        return next;
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      cartItems,
      isOpen,
      openDrawer,
      closeDrawer,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      persistCartItems,
      clearCart,
    }),
    [
      cartItems,
      isOpen,
      openDrawer,
      closeDrawer,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      persistCartItems,
      clearCart,
    ]
  );

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
      <CartDrawer />
    </CartDrawerContext.Provider>
  );
};

export const useCartDrawer = () => {
  const context = useContext(CartDrawerContext);
  if (!context) {
    throw new Error("useCartDrawer must be used within CartDrawerProvider");
  }
  return context;
};
